import { AddToCartItem, ProductBuilderMachineContext } from '../types';

type CurtainAddonProducts = (
  context: ProductBuilderMachineContext,
  upi: string,
  roomName: string,
  parentProductTitle: string,
) => AddToCartItem[];

export const getCurtainAddonProducts: CurtainAddonProducts = (
  context,
  upi,
  roomName,
  parentProductTitle,
) => {
  const {
    measureInsurance: measureInsuranceAddonProducts,
    ceilingMountPleatedCurtainRail: ceilingMountPleatedCurtainRailAddonProducts,
    faceMountPleatedCurtainRail: faceMountPleatedCurtainRailAddonProducts,
    ceilingMountWaveFoldCurtainRail: ceilingMountWaveFoldCurtainRailAddonProducts,
    faceMountWaveFoldCurtainRail: faceMountWaveFoldCurtainRailAddonProducts,
    ceilingMountPleatedCurtainDoubleRail: ceilingMountPleatedCurtainDoubleRailAddonProducts,
    faceMountPleatedCurtainDoubleRail: faceMountPleatedCurtainDoubleRailAddonProducts,
    ceilingMountWaveFoldCurtainDoubleRail: ceilingMountWaveFoldCurtainDoubleRailAddonProducts,
    faceMountWaveFoldCurtainDoubleRail: faceMountWaveFoldCurtainDoubleRailAddonProducts,
    wandblack,
    wandwhite,
    trackblack,
    trackwhite,
    curtainblock,
    curtainsemi,
    quantity,
    custom,
  } = context;
  
  const { 
    measureInsurance, 
    track, 
    trackColour, 
    wand_option, 
    wand_colour, 
    wand_colour_price, 
    wand_colour_variant_id, 
    track_colour_variant_id, 
    track_colour_price, 
    lining, 
    liningColour, 
    lining_colour_variant_id, 
    lining_colour_price 
  } = custom as any;
  const baseAddonProducts = measureInsuranceAddonProducts
    .concat(ceilingMountPleatedCurtainRailAddonProducts)
    .concat(faceMountPleatedCurtainRailAddonProducts)
    .concat(ceilingMountWaveFoldCurtainRailAddonProducts)
    .concat(faceMountWaveFoldCurtainRailAddonProducts)
    .concat(ceilingMountPleatedCurtainDoubleRailAddonProducts)
    .concat(faceMountPleatedCurtainDoubleRailAddonProducts)
    .concat(ceilingMountWaveFoldCurtainDoubleRailAddonProducts)
    .concat(faceMountWaveFoldCurtainDoubleRailAddonProducts);

  // Handle wand colour using stored variant ID
  const getWandColourAddon = () => {
    // Handle edge case: ensure wand_option defaults to 'No' if empty/undefined
    const effectiveWandOption = wand_option || 'No';
    
    if (!effectiveWandOption || effectiveWandOption === 'No' || !wand_colour || wand_colour === 'no' || !wand_colour_variant_id || wand_colour_variant_id === 0) {
      return null;
    }
    
    // Validate variant ID is a positive number
    const variantId = Number(wand_colour_variant_id);
    if (isNaN(variantId) || variantId <= 0) {
      return null;
    }
    
    // Use the stored variant ID directly for consistency
    return {
      quantity: 1,
      id: variantId,
      properties: {
        _upi: upi,
        'Product for': `${parentProductTitle} - Room: ${roomName}`,
      }
    };
  };

  // Handle track colour using stored variant ID
  const getTrackColourAddon = () => {
    if (!trackColour || !track_colour_variant_id || track_colour_variant_id === 0) {
      return null;
    }
    
    // Validate variant ID is a positive number
    const variantId = Number(track_colour_variant_id);
    if (isNaN(variantId) || variantId <= 0) {
      return null;
    }
    
    // Use the stored variant ID directly for consistency
    return {
      quantity: 1,
      id: variantId,
      properties: {
        _upi: upi,
        'Product for': `${parentProductTitle} - Room: ${roomName}`,
      }
    };
  };

  // Handle lining colour using stored variant ID
  const getLiningColourAddon = () => {
    if (!liningColour || !lining_colour_variant_id || lining_colour_variant_id === 0) {
      return null;
    }
    
    // Validate variant ID is a positive number
    const variantId = Number(lining_colour_variant_id);
    if (isNaN(variantId) || variantId <= 0) {
      return null;
    }
    
    // Use the stored variant ID directly for consistency
    return {
      quantity: 1,
      id: variantId,
      properties: {
        _upi: upi,
        'Product for': `${parentProductTitle} - Room: ${roomName}`,
      }
    };
  };

  // Check if this is a curtain type that should NOT have left/right addon products
  const isCurtain = context.products[0]?.type === 'Curtain';

  const wandColourAddon = getWandColourAddon();
  const trackColourAddon = getTrackColourAddon();
  const liningColourAddon = getLiningColourAddon();

  // If it's a curtain type and Centre Opening is selected, don't add left/right rail addons
  const shouldSkipRailAddons = isCurtain && context.custom.configuration?.includes('Centre Opening');

  return baseAddonProducts
    .filter((product) => {
      // Check if first_available_variant exists and has an id
      if (!product.first_available_variant || !product.first_available_variant.id) {
        return false;
      }
      
      const { id } = product.first_available_variant;
      const isMeasureInsurance = id === measureInsurance;

      let isCurtainRail;
      if (typeof track !== 'undefined') {
        isCurtainRail = product.variants.find(
          variant => variant.id === Number(track.split(':')[1]),
        );
      }

      // Skip curtain rail addons for special curtain types with Centre Opening
      if (shouldSkipRailAddons && isCurtainRail) {
        return false;
      }

      return isMeasureInsurance || isCurtainRail;
    })
    .flatMap((product) => {
      // Check if first_available_variant exists and has an id
      if (!product.first_available_variant || !product.first_available_variant.id) {
        return []; // Return empty array instead of null
      }
      
      const { first_available_variant, variants } = product;
      
      const { id } = first_available_variant;
      const isMeasureInsurance = id === measureInsurance;
      let isCurtainRail;
      if (typeof track !== 'undefined') {
        isCurtainRail = variants.find(
          variant => variant.id === Number(track.split(':')[1]),
        );
      }

      let addonId;
      switch (true) {
        case isCurtainRail !== undefined:
          addonId = Number(track.split(':')[1]);
          break;
        default:
          addonId = id;
          break;
      }
      return [{
        quantity: isMeasureInsurance || isCurtainRail ? 1 : quantity,
        id: addonId,
        properties: !isMeasureInsurance
          ? {
              _upi: upi,
              'Product for': `${parentProductTitle} - Room: ${roomName}`,
            }
          : {
              _upi: '',
              'Product for': `${parentProductTitle} - Room: ${roomName}`,
            },
      }];
    })
    .concat(wandColourAddon ? [wandColourAddon] : [])
    .concat(trackColourAddon ? [trackColourAddon] : [])
    .concat(liningColourAddon ? [liningColourAddon] : []);
};
